import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import {
  CampaignMatchList,
  useCampaignMatchListOptions,
} from '@/components/tree-dnd/tree-dnd-conditions/campaign-match-list';
import { MatchModeInlineHelp } from '@/components/tree-dnd/tree-dnd-conditions/match-mode-inline-help';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import {
  useMatchListSummary,
  useNaturalMatchListSummary,
} from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface ReceivedNotificationConditionProps {
  node: Extract<TreeConditionNode, { kind: 'receivedNotification' }>;
  onChange: (
    value: Extract<TreeConditionNode, { kind: 'receivedNotification' }>['args']
  ) => void;
}

export function ReceivedNotificationCondition({
  node,
  onChange,
}: ReceivedNotificationConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const value = node.args;
  const campaignOptions = useCampaignMatchListOptions(value.campaigns.ids);
  const summaryDetail = useMatchListSummary(
    value.campaigns,
    campaignOptions,
    LL.audiences.filters.conditionSummary.noCampaignsSelected()
  );
  const campaignsNaturalSummary = useNaturalMatchListSummary(
    value.campaigns,
    campaignOptions,
    LL.audiences.filters.conditionSummary.noCampaignsSelected()
  );
  const conditionLabel = LL.audiences.filters.conditions.receivedNotification();

  const filterSummary = useMemo(() => {
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.receivedNotificationExcludeSummary(
        { campaigns: campaignsNaturalSummary }
      );
    }
    return LL.audiences.filters.conditionSummary.receivedNotificationIncludeSummary(
      { campaigns: campaignsNaturalSummary }
    );
  }, [LL, campaignsNaturalSummary, node.negated]);

  const hasSelectedCampaigns = value.campaigns.ids.length > 0;
  const preview = useConditionAudiencePreview(
    node,
    open && hasSelectedCampaigns
  );

  return (
    <>
      <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.receivedNotificationDescription()}
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            error={
              hasSelectedCampaigns
                ? undefined
                : LL.audiences.filters.editSheet.noCampaignsSelectedError()
            }
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        <ConditionField
          label={LL.audiences.filters.editSheet.campaignsLabel()}
          helpPlacement="before"
          helpText={
            <MatchModeInlineHelp
              match={value.campaigns.match}
              onChange={(match) =>
                onChange({
                  ...value,
                  campaigns: { ...value.campaigns, match },
                })
              }
              before={LL.audiences.filters.conditionSummary.receivedNotificationMatchHelpBefore()}
              afterAny={LL.audiences.filters.conditionSummary.receivedNotificationMatchHelpAfterAny()}
              afterAll={LL.audiences.filters.conditionSummary.receivedNotificationMatchHelpAfterAll()}
            />
          }
        >
          <CampaignMatchList
            value={value.campaigns}
            onChange={(campaigns) => onChange({ ...value, campaigns })}
            allowAll={false}
          />
        </ConditionField>
      </ConditionEditSheet>
    </>
  );
}
